import Vue from 'vue';
import Vuex from 'vuex';
import { i18n } from '@/plugins/I18n.js';

Vue.use(Vuex);

function getDeviceType(device) {
  if (/iphone/i.test(device.model)) {
    return 'iPhone';
  }

  if (/ipad/i.test(device.model)) {
    return 'iPad';
  }

  if (/ipod/i.test(device.model)) {
    return 'iPod';
  }

  if (/appletv/i.test(device.model)) {
    return 'Apple TV';
  }

  if (/mac/i.test(device.model)) {
    return 'Mac';
  }

  return 'Unknown';
}

function getCompatibleCacheTypes(type) {
  if (type === 'iPhone') {
    return ['iPod'];
  }

  if (type === 'iPod') {
    return ['iPhone'];
  }

  return [];
}

function createStore() {
  return new Vuex.Store({
    state() {
      return {
        theme: localStorage.getItem('theme') || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'),
        packageVersion: process.env.PACKAGE_VERSION || '0',
        lang: localStorage.getItem('lang') || 'en',
        dictionary: {},
        lt: localStorage.getItem('lt'),
        ref: localStorage.getItem('ref'),
        devices: [],
        devicesLoaded: false,
        currentDevice: null,
        isSearchExpanded: false,
        genres: {
          ios: {}, cydia: {}, osx: {}, standalone: {}, tvos: {}, books: {},
        },
        config: {},
        configLoaded: false,
        promos: {
          pages: [],
          content: [],
          banners: [],
        },
        stats: {},
      };
    },
    mutations: {
      setTheme(state, payload) {
        const oldTheme = payload === 'light' ? 'dark' : 'light';
        document.documentElement.classList.remove(oldTheme);
        document.documentElement.classList.add(payload);
        localStorage.setItem('theme', payload);
        state.theme = payload;
      },
      setDevices(state, payload) {
        state.devices = payload;

        if (!state.devicesLoaded) {
          state.devicesLoaded = true;
          setTimeout(() => {
            const toProfile = Vue.router.currentRoute.matched[0].path === '/my';
            const { authRequired } = Vue.router.currentRoute.meta;
            if (!state.devices.length && toProfile && authRequired) {
              Vue.router.push('/my/error');
            }
          }, 200);
        }
      },
      setConfig(state, payload) {
        state.configLoaded = true;
        state.config = payload;
      },
      isSearchExpanded(state, payload) {
        state.isSearchExpanded = payload;
      },
      setGenres(state, payload) {
        state.genres = payload;
      },
      lt(state, payload) {
        localStorage.setItem('lt', payload);
        state.lt = payload;
      },
      ref(state, payload) {
        if (state.ref) {
          return;
        }
        localStorage.setItem('ref', payload);
        state.ref = payload;
      },
      lang(state, payload) {
        state.lang = payload;
      },
      promos(state, payload) {
        state.promos = payload;
      },
      stats(state, payload) {
        state.stats = payload;
      },
    },
    actions: {
      getGenres(context) {
        return Vue.prototype.$api.getGenres().then((data) => {
          const genres = {};
          /* eslint-disable-next-line */
          for (const category of Object.keys(data)) {
            genres[category] = Object.fromEntries(data[category].map(genre => [genre.id, genre]));
          }
          context.commit('setGenres', genres);
        });
      },
      getDevices(context, handleErrors) {
        if (!context.getters.lt) {
          context.commit('setDevices', []);
          return;
        }
        // eslint-disable-next-line
        return Vue.prototype.$api.getDevices(handleErrors).then((response) => {
          if (response.success) {
            const devices = (response.data || []).map((device) => {
              let jailbreak = 'JAILBROKEN';
              if (device.appsync === 'no') {
                jailbreak = 'NOT JAILBROKEN';
              }

              const type = getDeviceType(device);

              const newDevice = {
                lt: device.link_token,
                name: device.name,
                model: device.nice_idevice_model,
                ios: device.ios_version,
                jailbreak,
                jailbreakPrefix: device.appsync === 'unknown' ? '? ' : '',
                pro: device.is_pro === 'yes',
                plus: device.is_plus === 'yes' || Date.now() < +new Date(device.plus_till * 1000),
                email: device.email,
                type,
                compatibleCacheTypes: getCompatibleCacheTypes(type),
              };

              newDevice.icon = require('@/assets/iphone.png');
              if (/pad/i.test(newDevice.model)) {
                newDevice.icon = require('@/assets/ipad.png');
              } else if (/tv/i.test(newDevice.model)) {
                newDevice.icon = require('@/assets/appletv.png');
              } else if (/mac/i.test(newDevice.model)) {
                newDevice.icon = require('@/assets/mac.png');
              }

              newDevice.title = `${newDevice.model} (${newDevice.ios}`;

              return newDevice;
            });

            context.commit('setDevices', devices);
          } else {
            context.commit('setDevices', []);
          }
        });
      },
      getConfig(context, handleErrors) {
        return new Promise((resolve, reject) => {
          if (!context.getters.lt) {
            context.commit('setConfig', {});
            resolve();
            return;
          }
          Vue.prototype.$api.getConfiguration(handleErrors).then((config) => {
            const result = Object.keys(config).includes('success') ? config.data : config;
            context.commit('setConfig', result);
            resolve(result);
          }).catch(reject);
        });
      },
      setLang(context, payload) {
        localStorage.setItem('lang', payload);
        context.commit('lang', payload);
        i18n.changeLanguage(payload);
      },
      getStats(context) {
        if (context.stats) {
          return Promise.resolve(context.stats);
        }

        return Vue.prototype.$api.getStats().then((response) => {
          context.commit('stats', response);

          return response;
        });
      },
    },
    getters: {
      appVersion: state => state.packageVersion,
      isDeviceLinked(state) {
        return !!Object.keys(state.config).length || state.devices.length;
      },
      devices(state) {
        return state.devices;
      },
      devicesLoaded(state) {
        return state.devicesLoaded;
      },
      config(state) {
        return state.config;
      },
      configLoaded(state) {
        return state.configLoaded;
      },
      isSearchExpanded(state) {
        return state.isSearchExpanded;
      },
      genres(state) {
        return state.genres;
      },
      lt(state) {
        return state.lt;
      },
      ref(state) {
        return state.ref;
      },
      lang(state) {
        return state.lang;
      },
      promos(state) {
        return state.promos;
      },
      hasActivePlus(state) {
        // eslint-disable-next-line camelcase
        return state.config?.is_plus === 'yes';
      },
      hasCanceledPlus(state) {
        // eslint-disable-next-line camelcase
        return Date.now() < +new Date(state.config?.plus_till * 1000);
      },
      hasPlus(state, getters) {
        return getters.hasActivePlus || getters.hasCanceledPlus;
      },
      hasPro(state) {
        // eslint-disable-next-line camelcase
        return state.config?.is_pro === 'yes';
      },
      email(state) {
        return state.config?.email || '';
      },
    },
  });
}

export default createStore;
